import { useCallback, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useSearchPageData } from '#src/app/pages/searchV2/pageContext';
import { useSearchActions } from '#src/app/pages/searchV2/shared/searchState';

import { useResolveCityState } from './hooks/useResolveCityState';

type Result = ModelHookResult<
	{ isNearYouLoading: boolean },
	{
		filterByNearYou: () => void;
	}
>;

export function useFilterByNearYou(): Result {
	const {
		data: { viewer },
	} = useSearchPageData();
	const track = useTracking();
	const actions = useSearchActions();

	const {
		actions: { resolveCityState },
	} = useResolveCityState();

	const [isNearYouLoading, setIsNearYouLoading] = useState(false);

	const filterByCityResolved = useCallback(
		async (city: string, countryCode: string, stateCode?: string) => {
			setIsNearYouLoading(true);
			const cityState = await resolveCityState({ city, countryCode, stateCode });
			actions.setLocationCity(cityState);
			setIsNearYouLoading(false);
		},
		[actions, resolveCityState],
	);

	return {
		data: { isNearYouLoading },
		actions: {
			filterByNearYou: useCallback(() => {
				if (isNearYouLoading) return;

				void track('search', { q: '', context: 'landing_near_me_button' });

				const city = viewer.user?.city;
				const stateCode = viewer.user?.stateCode ? viewer.user.stateCode : undefined;
				const countryCode = viewer.user?.country?.countryCode || viewer.country.countryCode;

				if (city) {
					void filterByCityResolved(city, countryCode, stateCode);
					return;
				}
				actions.setLocationCountry(countryCode);
			}, [filterByCityResolved, actions, isNearYouLoading, track, viewer]),
		},
	};
}
